import { PUBLIC_PROFILE_FIELDS } from '../../api/_lib/publicProfileFields';

let supabaseClientLoader;
const loadSupabaseClient = () => {
  if (!supabaseClientLoader) {
    supabaseClientLoader = import('./supabase').then((module) => module.supabase);
  }
  return supabaseClientLoader;
};

async function fetchJson(url) {
  const response = await fetch(url);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to load public profile.');
  }
  return payload;
}

function normalizePublicUsername(username = '') {
  return String(username).trim().toLowerCase().replace(/^@/, '').slice(0, 64);
}

function makeClientVersion(profile, links) {
  const linksUpdatedAt = (links || []).reduce((latest, link) => {
    const updatedAt = link?.updated_at || link?.created_at || '';
    return updatedAt > latest ? updatedAt : latest;
  }, '');

  const profileUpdatedAt = profile?.updated_at || null;
  const linksCount = links?.length || 0;

  return {
    version: `client-${profileUpdatedAt || 'profile'}-${linksUpdatedAt || 'links'}-${linksCount}`,
    profileUpdatedAt,
    linksUpdatedAt: linksUpdatedAt || null,
    linksCount,
  };
}

async function selectPublicProfileFromSupabase(normalizedUsername) {
  const supabase = await loadSupabaseClient();
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('*')
    .ilike('username', normalizedUsername)
    .maybeSingle();

  return { profile, profileError };
}

function toPublicProfile(profile) {
  return PUBLIC_PROFILE_FIELDS.reduce((publicProfile, field) => {
    if (Object.prototype.hasOwnProperty.call(profile, field)) {
      publicProfile[field] = profile[field];
    }
    return publicProfile;
  }, {});
}

async function fetchPublicProfileFromSupabase(username) {
  const normalizedUsername = normalizePublicUsername(username);
  if (!normalizedUsername) {
    throw new Error('Username required.');
  }

  const { profile, profileError } = await selectPublicProfileFromSupabase(normalizedUsername);

  if (profileError) throw profileError;
  if (!profile || profile.is_terminated) {
    throw new Error('Profile not found.');
  }

  const publicProfile = toPublicProfile(profile);
  const supabase = await loadSupabaseClient();

  const { data: links, error: linksError } = await supabase
    .from('links')
    .select('*')
    .eq('profile_id', profile.id)
    .order('order_index', { ascending: true });

  if (linksError) throw linksError;

  const safeLinks = links || [];
  return {
    success: true,
    profile: publicProfile,
    links: safeLinks,
    ...makeClientVersion(publicProfile, safeLinks),
  };
}

export async function fetchPublicProfile(username) {
  try {
    return await fetchJson(`/api/v1/public-profile?username=${encodeURIComponent(username)}`);
  } catch (error) {
    console.warn('Public profile API unavailable, using Supabase fallback.', error);
    return fetchPublicProfileFromSupabase(username);
  }
}

export async function fetchPublicProfileVersion(username) {
  try {
    return await fetchJson(`/api/v1/public-profile-version?username=${encodeURIComponent(username)}`);
  } catch {
    const payload = await fetchPublicProfileFromSupabase(username);
    return {
      success: true,
      username: payload.profile?.username || normalizePublicUsername(username),
      version: payload.version,
      profileUpdatedAt: payload.profileUpdatedAt,
      linksUpdatedAt: payload.linksUpdatedAt,
      linksCount: payload.linksCount,
    };
  }
}
